import React from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { Navbar } from "@/components/layout/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import NotFound from "@/pages/not-found";
import Home from "@/pages/Home";
import LandingPage from "@/pages/LandingPage";
import UserLogin from "@/pages/UserLogin";
import ShopDashboard from "@/pages/ShopDashboard";
import ShopPOS from "@/pages/ShopPOS";
import ShopCustomers from "@/pages/ShopCustomers";
import ShopInventory from "@/pages/ShopInventory";
import ShopInventoryAnalytics from "@/pages/ShopInventoryAnalytics";
import ShopInventoryTransfers from "@/pages/ShopInventoryTransfers";
import ShopInventorySuppliers from "@/pages/ShopInventorySuppliers";
import ShopInventoryMovements from "@/pages/ShopInventoryMovements";
import ShopSettings from "@/pages/ShopSettings";
import ShopUsers from "@/pages/ShopUsers";
import RegisterShop from "@/pages/RegisterShop";
import ShopReports from "@/pages/ShopReports_NEW";
import { getCurrentShop, getCurrentUser, getCurrentOwner } from "@/lib/auth";
import ShopSuspended from "./pages/ShopSuspended";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminShops from "./pages/AdminShops";
import AdminPayments from "./pages/AdminPayments";
import AdminLogs from "./pages/AdminLogs";
import OwnerDashboard from "@/pages/OwnerDashboard";
import OwnerCreateShop from "@/pages/OwnerCreateShop";
import OwnerDiscrepancies from "@/pages/OwnerDiscrepancies";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import { OfflineProvider } from "@/lib/contexts/offline-context";
import { indexedDBService } from "@/lib/db/indexed-db";
import { useEffect } from "react";
import { UpdateBanner } from "./components/ui/UpdateBanner";
import PlansPage from "./pages/Plans";
import { Shield } from "lucide-react";
import AccountSuspended from "./pages/AccountSuspended";
import VerifyEmailPending from "./pages/VerifyEmailPending";
import VerifyEmail from "./pages/VerifyEmail";
import ShopReturns from "@/pages/ShopReturns";
import SupervisorDashboard from "@/pages/SupervisorDashboard";
import { ProtectedShopRoute } from "./components/ProtectedShopRoute";

function getCurrentAdmin() {
  const authHeader = localStorage.getItem("admin_auth");
  const adminUser = localStorage.getItem("admin_user");
  if (authHeader && adminUser) {
    try {
      return JSON.parse(adminUser);
    } catch {
      return null;
    }
  }
  return null;
}

function ProtectedAdminRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const admin = getCurrentAdmin();
  if (!admin) {
    return <Redirect to="/admin/login" />;
  }
  return <Component />;
}

function ProtectedRoute({
  component: Component,
  requiredRoles = [],
}: {
  component: React.ComponentType;
  requiredRoles?: Array<"supervisor" | "store_manager" | "cashier">;
}) {
  const shop = getCurrentShop();
  const user = getCurrentUser();
  if (!shop || !user) {
    return <Redirect to="/shop/login" />;
  }
  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role as any)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground mb-4">
              No tienes permiso para acceder a esta página.
            </p>
            <Button onClick={() => window.history.back()}>Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <Component />;
}

function ProtectedOwnerRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const owner = getCurrentOwner();
  if (!owner) {
    return <Redirect to="/shop/login" />;
  }
  return <Component />;
}

function Router() {
  const [location] = useLocation();
  const routesWithoutSystemNavbar = [
    "/",
    "/admin/dashboard",
    "/admin/shops",
    "/admin/payments",
    "/admin/logs",
    "/owner/create-shop",
    "/owner/discrepancies",
    "/owner/plans",
    "/account-suspended",
    "/shop/suspended",
    "/shop/login",
    "/register",
    "/supervisor/dashboard",
  ];
  const shouldShowSystemNavbar = !routesWithoutSystemNavbar.some(
    (route) => location === route || location.startsWith(route + "/"),
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <OfflineIndicator />
      <UpdateBanner />
      <div className="flex-1">
        {shouldShowSystemNavbar && <Navbar />}
        <div className="flex-1">
          <Switch>
            <Route path="/" component={LandingPage} />
            <Route path="/home" component={Home} />
            <Route path="/register" component={RegisterShop} />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route
              path="/verify-email-pending"
              component={VerifyEmailPending}
            />
            <Route path="/shop/login" component={UserLogin} />
            <Route path="/shop/suspended">
              {() => {
                setTimeout(() => (window.location.href = "/shop/login"), 3000);
                return <ShopSuspended />;
              }}
            </Route>
            <Route path="/shop/dashboard">
              {() => (
                <ProtectedShopRoute
                  component={ShopDashboard}
                  requiredRoles={["supervisor", "store_manager", "cashier"]}
                />
              )}
            </Route>
            <Route path="/shop/pos">
              {() => (
                <ProtectedShopRoute
                  component={ShopPOS}
                  requiredRoles={["supervisor", "store_manager", "cashier"]}
                />
              )}
            </Route>
            <Route path="/shop/inventory">
              {() => (
                <ProtectedShopRoute
                  component={ShopInventory}
                  requiredRoles={["supervisor", "store_manager", "cashier"]}
                />
              )}
            </Route>
            {/* === NUEVAS RUTAS PARA GESTIÓN DE INVENTARIO === */}
            <Route path="/shop/inventory/movements">
              {() => (
                <ProtectedShopRoute
                  component={ShopInventoryMovements}
                  requiredRoles={["supervisor", "store_manager", "cashier"]}
                />
              )}
            </Route>
            <Route path="/shop/inventory/transfers">
              {() => (
                <ProtectedShopRoute
                  component={ShopInventoryTransfers}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            <Route path="/shop/inventory/suppliers">
              {() => (
                <ProtectedShopRoute
                  component={ShopInventorySuppliers}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            <Route path="/shop/inventory/analytics">
              {() => (
                <ProtectedShopRoute
                  component={ShopInventoryAnalytics}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            {/* === FIN NUEVAS RUTAS === */}
            <Route path="/shop/settings">
              {() => (
                <ProtectedShopRoute
                  component={ShopSettings}
                  requiredRoles={["supervisor"]}
                />
              )}
            </Route>
            <Route path="/shop/users">
              {() => (
                <ProtectedShopRoute
                  component={ShopUsers}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            <Route path="/shop/customers">
              {() => (
                <ProtectedShopRoute
                  component={ShopCustomers}
                  requiredRoles={["supervisor", "store_manager", "cashier"]}
                />
              )}
            </Route>
            <Route path="/shop/reports">
              {() => (
                <ProtectedShopRoute
                  component={ShopReports}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            <Route path="/shop/returns">
              {() => (
                <ProtectedShopRoute
                  component={ShopReturns}
                  requiredRoles={["supervisor", "store_manager"]}
                />
              )}
            </Route>
            <Route path="/owner/dashboard">
              {() => <ProtectedOwnerRoute component={OwnerDashboard} />}
            </Route>
            <Route path="/owner/create-shop">
              {() => <ProtectedOwnerRoute component={OwnerCreateShop} />}
            </Route>
            <Route path="/owner/discrepancies">
              {() => <ProtectedOwnerRoute component={OwnerDiscrepancies} />}
            </Route>
            <Route path="/owner/plans">
              {() => <ProtectedOwnerRoute component={PlansPage} />}
            </Route>
            <Route path="/account-suspended" component={AccountSuspended} />
            <Route path="/admin/login">
              {() => {
                const admin = getCurrentAdmin();
                return admin ? (
                  <Redirect to="/admin/dashboard" />
                ) : (
                  <AdminLogin />
                );
              }}
            </Route>
            <Route path="/admin/dashboard">
              {() => <ProtectedAdminRoute component={AdminDashboard} />}
            </Route>
            <Route path="/admin/shops">
              {() => <ProtectedAdminRoute component={AdminShops} />}
            </Route>
            <Route path="/admin/payments">
              {() => <ProtectedAdminRoute component={AdminPayments} />}
            </Route>
            <Route path="/admin/logs">
              {() => <ProtectedAdminRoute component={AdminLogs} />}
            </Route>
            <Route path="/supervisor/dashboard">
              {() => {
                const user = getCurrentUser();
                return user?.role === "supervisor" ? (
                  <SupervisorDashboard />
                ) : (
                  <Redirect to="/shop/login" />
                );
              }}
            </Route>
            <Route component={NotFound} />
          </Switch>
        </div>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    indexedDBService.init().catch(console.error);
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <OfflineProvider>
            <Router />
          </OfflineProvider>
        </ThemeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
